import React, { useState, useEffect } from 'react';
import { Eye, Truck, Calendar, Package, Search, Filter, Camera, X, Upload, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { upcomingDeliveriesService } from '../../services';

const UpcomingDeliveries = () => {
	const { user } = useAuth();
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [selectedDelivery, setSelectedDelivery] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [checkedItems, setCheckedItems] = useState({});
	const [remarks, setRemarks] = useState('');
	const [packageImages, setPackageImages] = useState([]);
	const [receivedItemImages, setReceivedItemImages] = useState([]);
	const [deliveries, setDeliveries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const presetRemarks = [
		'Package is in good shape',
		'Package is in bad shape',
		'Dress is damaged',
		'Dress has stains',
		'All items present',
		'Accessories missing',
		'Seals tampered',
		'Incorrect item received'
	];
	const [selectedRemarkTags, setSelectedRemarkTags] = useState([]);

	const toggleRemarkTag = (tag) => {
		setSelectedRemarkTags((prev) => {
			const exists = prev.includes(tag);
			let next;
			if (exists) {
				next = prev.filter(t => t !== tag);
			} else {
				next = [...prev, tag];
			}
			// Build remarks string from selected tags plus any manual text that isn't a preset
			const manualParts = (remarks || '')
				.split(/[;,]/)
				.map(s => s.trim().replace(/[.]+$/,''))
				.filter(s => s && !presetRemarks.includes(s));
			const combined = [...next, ...manualParts];
			let text = combined.join(', ');
			if (text && !/[.]$/.test(text)) {
				text = text + '.';
			}
			setRemarks(text);
			return next;
		});
	};

	// Fetch upcoming deliveries from API
	const fetchDeliveries = async () => {
		setLoading(true);
		try {
			const response = await upcomingDeliveriesService.getAll();
			if (response.success) {
				// Transform API data to match component expectations
				const transformedDeliveries = response.data.map(item => ({
					id: item.order_id,
					awbNumber: item.awb_number,
					sellerName: item.seller_name,
					sellerEmail: item.seller_email,
					sellerMobile: item.seller_mobile,
					estimatedDelivery: new Date(item.sent_date).toLocaleDateString(),
					status: 'In Transit', // All items from this API are in-transit
					items: [
						{
							name: item.item_name,
							quantity: 1,
							color: item.color || 'N/A',
							size: item.size || 'N/A'
						}
					],
					trackingNumber: item.awb_number, // Using AWB as tracking number
					courier: item.courier_provider || 'N/A', // Get from API
					branch: 'Store Branch', // Default branch
					orderId: item.order_id,
					listingId: item.listing_id,
					totalAmount: item.total_amount,
					depositAmount: item.deposit_amount,
					remainingAmount: item.remaining_amount,
					paymentStatus: item.payment_status,
					authenticationStatus: item.authentication_status,
					createdAt: item.created_at,
					updatedAt: item.updated_at,
					sentDate: item.sent_date
				}));
				setDeliveries(transformedDeliveries);
			} else {
				toast.error('Failed to fetch upcoming deliveries');
				setDeliveries([]);
			}
		} catch (error) {
			console.error('Error fetching deliveries:', error);
			toast.error('Error fetching upcoming deliveries');
			setDeliveries([]);
		} finally {
			setLoading(false);
		}
	};

	// Load deliveries on component mount
	useEffect(() => {
		fetchDeliveries();
	}, []);

	const filteredDeliveries = deliveries.filter(delivery => {
		const matchesSearch = delivery.awbNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
			delivery.sellerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			delivery.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesStatus = statusFilter === 'all' || delivery.status.toLowerCase() === statusFilter.toLowerCase();
		return matchesSearch && matchesStatus;
	});

	const getStatusColor = (status) => {
		switch (status) {
			case 'Processing': return '#f59e0b';
			case 'Dispatched': return '#3b82f6';
			case 'In Transit': return '#8b5cf6';
			case 'Out for Delivery': return '#10b981';
			default: return '#6b7280';
		}
	};

	const openModal = (delivery) => {
		// Initialize checked state for each item as true (checked by default)
		const initialChecked = (delivery.items || []).reduce((acc, item, index) => {
			acc[index] = true;
			return acc;
		}, {});
		setCheckedItems(initialChecked);
		setRemarks('');
		setPackageImages([]);
		setReceivedItemImages([]);
		setSelectedDelivery(delivery);
		setShowModal(true);
	};

	const closeModal = () => {
		setSelectedDelivery(null);
		setShowModal(false);
		setCheckedItems({});
		setRemarks('');
		setPackageImages([]);
		setReceivedItemImages([]);
	};

	const handlePackageImageUpload = (e) => {
		const files = Array.from(e.target.files);
		files.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setPackageImages(prev => [...prev, { file, preview: reader.result }]);
			};
			reader.readAsDataURL(file);
		});
	};

	const handleReceivedItemImageUpload = (e) => {
		const files = Array.from(e.target.files);
		files.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setReceivedItemImages(prev => [...prev, { file, preview: reader.result }]);
			};
			reader.readAsDataURL(file);
		});
	};

	const removePackageImage = (index) => {
		setPackageImages(prev => prev.filter((_, i) => i !== index));
	};

	const removeReceivedItemImage = (index) => {
		setReceivedItemImages(prev => prev.filter((_, i) => i !== index));
	};

	const handleConfirmReceived = async () => {
		const total = selectedDelivery?.items?.length || 0;
		const receivedCount = Object.values(checkedItems).filter(Boolean).length;

		if (receivedCount === 0) {
			toast.error('Please tick at least one item as received');
			return;
		}

		if (packageImages.length < 2) {
			toast.error('Please upload at least 2 package images');
			return;
		}

		setSubmitting(true);

		try {
			const payload = {
				receivedItems: checkedItems,
				remarks,
				packageImages: packageImages.map(img => img.preview), // Convert to base64 strings
				receivedItemImages: receivedItemImages.map(img => img.preview),
				receivedBy: user?.name || 'Store User',
				receivedByRole: user?.role || 'Receiver',
				receivedCount,
				total,
				receivedAt: new Date().toISOString(),
				fullyVerified: receivedCount === total
			};

			const response = await upcomingDeliveriesService.markReceived(selectedDelivery.orderId, payload);

			if (response.success) {
				toast.success(`Confirmed ${receivedCount}/${total} items received by store and moved to Branch Stock`);
				closeModal();
				// Refresh the deliveries list
				await fetchDeliveries();
			} else {
				toast.error(response.message || 'Failed to confirm receipt');
			}
		} catch (error) {
			console.error('Error confirming receipt:', error);
			toast.error('Error confirming receipt. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div>
			<div className="card" style={{ marginBottom: '24px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
					<div>
						<h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
							Upcoming Deliveries
						</h1>
						<p style={{ color: '#6b7280' }}>
							Track and manage incoming deliveries from sellers (In-Transit Items)
						</p>
					</div>
					<button
						onClick={() => fetchDeliveries()}
						className="btn btn-secondary"
						disabled={loading}
						title="Refresh Deliveries"
						style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
					>
						<RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
						Refresh
					</button>
				</div>
			</div>

			{/* Search and Filter Section */}
			<div className="card" style={{ marginBottom: '24px' }}>
				<div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
					<div style={{ position: 'relative', flex: '1', minWidth: '250px' }}>
						<Search
							size={16}
							style={{
								position: 'absolute',
								left: '12px',
								top: '50%',
								transform: 'translateY(-50%)',
								color: '#9ca3af'
							}}
						/>
						<input
							type="text"
							placeholder="Search by AWB, seller name, or tracking number..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							style={{
								width: '100%',
								height: '44px',
								padding: '12px 12px 12px 40px',
								border: '1px solid #d1d5db',
								borderRadius: '8px',
								fontSize: '14px',
								outline: 'none',
								transition: 'border-color 0.2s'
							}}
						/>
					</div>

					{/* <div style={{ position: 'relative', minWidth: '250px' }}>
						<Filter
							size={16}
							style={{
								position: 'absolute',
								left: '12px',
								top: '50%',
								transform: 'translateY(-50%)',
								color: '#9ca3af'
							}}
						/>
						<select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							style={{
								width: '100%',
								height: '44px',
								padding: '12px 12px 12px 40px',
								border: '1px solid #d1d5db',
								borderRadius: '8px',
								fontSize: '14px',
								outline: 'none',
								backgroundColor: 'white',
								cursor: 'pointer'
							}}
						>
							<option value="all">All Status</option>
							<option value="processing">Processing</option>
							<option value="dispatched">Dispatched</option>
							<option value="in transit">In Transit</option>
							<option value="out for delivery">Out for Delivery</option>
						</select>
					</div> */}
				</div>
			</div>

			{/* Loading State */}
			{loading && (
				<div className="card">
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						padding: '40px',
						fontSize: '16px',
						color: '#6b7280'
					}}>
						<RefreshCw size={20} className="animate-spin" style={{ marginRight: '8px' }} />
						Loading upcoming deliveries...
					</div>
				</div>
			)}

			{/* Deliveries Table */}
			{!loading && (
				<div className="card">
					<div style={{ overflowX: 'auto' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse' }}>
							<thead>
								<tr style={{ borderBottom: '1px solid #e5e7eb' }}>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>AWB Number</th>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Seller</th>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Item Details</th>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Sent Date</th>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
									<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Amount</th>
									<th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '100px' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredDeliveries.map((delivery) => (
									<tr key={delivery.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
										<td style={{ padding: '12px' }}>
											<div style={{ fontFamily: 'monospace', fontWeight: '500', color: '#111827' }}>
												{delivery.awbNumber}
											</div>
										</td>
										<td style={{ padding: '12px' }}>
											<div>
												<div style={{ fontWeight: '500', color: '#111827' }}>{delivery.sellerName}</div>
												<div style={{ fontSize: '12px', color: '#6b7280' }}>{delivery.sellerEmail}</div>
											</div>
										</td>
										<td style={{ padding: '12px' }}>
											<div>
												<div style={{ fontWeight: '500', color: '#111827' }}>{delivery.items[0]?.name || 'N/A'}</div>
												<div style={{ fontSize: '12px', color: '#6b7280' }}>Order: {delivery.orderId}</div>
											</div>
										</td>
										<td style={{ padding: '12px' }}>
											<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<Calendar size={14} color="#6b7280" />
												<span style={{ color: '#111827' }}>{delivery.estimatedDelivery}</span>
											</div>
										</td>
										<td style={{ padding: '12px' }}>
											<span
												style={{
													padding: '4px 8px',
													borderRadius: '6px',
													fontSize: '12px',
													fontWeight: '500',
													backgroundColor: getStatusColor(delivery.status) + '20',
													color: getStatusColor(delivery.status)
												}}
											>
												{delivery.status}
											</span>
										</td>
										<td style={{ padding: '12px' }}>
											<div>
												<div style={{ fontWeight: '500', color: '#111827' }}>₹{delivery.totalAmount}</div>
												<div style={{ fontSize: '12px', color: '#6b7280' }}>Deposit: ₹{delivery.depositAmount}</div>
											</div>
										</td>
										<td style={{ padding: '12px', textAlign: 'center' }}>
											<button
												onClick={() => openModal(delivery)}
												style={{
													background: 'none',
													border: 'none',
													cursor: 'pointer',
													padding: '6px',
													borderRadius: '6px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													transition: 'background-color 0.2s'
												}}
												onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
												onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
												title="View Details"
											>
												<Eye size={16} color="#6b7280" />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{filteredDeliveries.length === 0 && (
						<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
							<Truck size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
							<p>No deliveries found matching your criteria</p>
						</div>
					)}
				</div>
			)}

			{/* Delivery Details Modal */}
			{showModal && selectedDelivery && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 1000,
						padding: '20px'
					}}
					onClick={closeModal}
				>
					<div
						className="card"
						style={{
							width: '80%',
							maxWidth: '1200px',
							maxHeight: '80vh',
							overflowY: 'auto',
							margin: '0',
							position: 'relative'
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
							<h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
								Delivery Details
							</h2>
							<button
								onClick={closeModal}
								style={{
									background: 'none',
									border: 'none',
									fontSize: '24px',
									cursor: 'pointer',
									color: '#6b7280',
									padding: '4px'
								}}
							>
								×
							</button>
						</div>

						<div style={{ marginBottom: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', backgroundColor: '#f9fafb' }}>
							<h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '14px' }}>
								Shipment Information
							</h3>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										AWB Number
									</label>
									<div style={{ fontFamily: 'monospace', fontWeight: '500', color: '#111827' }}>
										{selectedDelivery.awbNumber}
									</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Tracking Number
									</label>
									<div style={{ fontFamily: 'monospace', fontWeight: '500', color: '#111827' }}>
										{selectedDelivery.trackingNumber}
									</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Courier
									</label>
									<div style={{ color: '#111827' }}>{selectedDelivery.courier}</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Branch
									</label>
									<div style={{ color: '#111827' }}>{selectedDelivery.branch}</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Estimated Delivery
									</label>
									<div style={{ color: '#111827' }}>{selectedDelivery.estimatedDelivery}</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Status
									</label>
									<div>
										<span
											style={{
												padding: '4px 8px',
												borderRadius: '6px',
												fontSize: '12px',
												fontWeight: '500',
												backgroundColor: getStatusColor(selectedDelivery.status) + '20',
												color: getStatusColor(selectedDelivery.status)
											}}
										>
											{selectedDelivery.status}
										</span>
									</div>
								</div>
							</div>
						</div>

						<div style={{ marginBottom: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', backgroundColor: '#ffffff' }}>
							<h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '14px' }}>
								Seller Information
							</h3>
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Name
									</label>
									<div style={{ color: '#111827' }}>{selectedDelivery.sellerName}</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Email
									</label>
									<div style={{ color: '#111827' }}>{selectedDelivery.sellerEmail}</div>
								</div>
								<div>
									<label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
										Mobile
									</label>
									<div style={{ color: '#111827', fontFamily: 'monospace' }}>{selectedDelivery.sellerMobile || 'N/A'}</div>
								</div>
							</div>
						</div>

						<div>
							<h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
								Items in Shipment (to be received by store)
							</h3>
							<div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
								<table style={{ width: '100%', borderCollapse: 'collapse' }}>
									<thead>
										<tr style={{ backgroundColor: '#f9fafb' }}>
											<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Item</th>
											<th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Quantity</th>
											<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Color</th>
											<th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Size</th>
										</tr>
									</thead>
									<tbody>
										{selectedDelivery.items.map((item, index) => (
											<tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
												<td style={{ padding: '12px', color: '#111827' }}>
													<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
														<input
															type="checkbox"
															checked={!!checkedItems[index]}
															onChange={(e) => {
																const isChecked = e.target.checked;
																setCheckedItems(prev => ({ ...prev, [index]: isChecked }));
															}}
															aria-label={`Select ${item.name}`}
															style={{ width: '16px', height: '16px' }}
														/>
														<span>{item.name}</span>
													</div>
												</td>
												<td style={{ padding: '12px', textAlign: 'center', color: '#111827' }}>{item.quantity}</td>
												<td style={{ padding: '12px', color: '#111827' }}>{item.color}</td>
												<td style={{ padding: '12px', color: '#111827' }}>{item.size}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{/* Summary and Remarks */}
							<div style={{
								marginTop: '16px',
								display: 'grid',
								gridTemplateColumns: '1fr',
								gap: '12px'
							}}>
								<div style={{
									backgroundColor: '#f0f9ff',
									border: '1px solid #bae6fd',
									borderRadius: '8px',
									padding: '12px',
									fontSize: '14px',
									color: '#0369a1'
								}}>
									Items Received: {Object.values(checkedItems).filter(Boolean).length} / {selectedDelivery?.items?.length || 0}
								</div>
							<div>
								<label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Remarks for receiver (optional)</label>
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
									{presetRemarks.map((tag) => {
										const active = selectedRemarkTags.includes(tag);
										return (
											<button
												key={tag}
												type="button"
												onClick={() => toggleRemarkTag(tag)}
												style={{
													padding: '6px 10px',
													borderRadius: '16px',
													border: active ? '1px solid #2563eb' : '1px solid #d1d5db',
													backgroundColor: active ? '#2563eb10' : 'white',
													color: '#111827',
													fontSize: '12px',
													cursor: 'pointer'
												}}
											>
												{tag}
											</button>
										);
									})}
								</div>
								<textarea
									value={remarks}
									onChange={(e) => setRemarks(e.target.value)}
									rows={3}
									style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px', outline: 'none' }}
									placeholder="Add notes about condition, discrepancies, or missing items received from seller"
								/>
							</div>

								{/* Image Upload Sections - Side by Side */}
								<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
									{/* Package Images Upload */}
									<div>
										<label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
											Package Images Received by Store (Minimum 2) *
										</label>
									<div style={{
										border: '2px dashed #d1d5db',
										borderRadius: '8px',
										padding: '16px',
										textAlign: 'center',
										marginBottom: '12px'
									}}>
										<input
											type="file"
											multiple
											accept="image/*"
											onChange={handlePackageImageUpload}
											style={{ display: 'none' }}
											id="package-image-upload"
										/>
										<label htmlFor="package-image-upload" style={{ cursor: 'pointer' }}>
											<Camera size={24} color="#6b7280" style={{ marginBottom: '8px' }} />
											<p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
												Upload package images
											</p>
											<p style={{ color: '#9ca3af', fontSize: '12px', margin: '4px 0 0' }}>
												Images of the package/box received from seller (minimum 2)
											</p>
										</label>
									</div>

									{/* Package Images Preview */}
									{packageImages.length > 0 && (
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
											gap: '8px',
											marginBottom: '12px'
										}}>
											{packageImages.map((image, index) => (
												<div key={index} style={{ position: 'relative' }}>
													<img
														src={image.preview}
														alt={`Package ${index + 1}`}
														style={{
															width: '100%',
															height: '80px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb'
														}}
													/>
													<button
														type="button"
														onClick={() => removePackageImage(index)}
														style={{
															position: 'absolute',
															top: '4px',
															right: '4px',
															background: 'rgba(239, 68, 68, 0.9)',
															color: 'white',
															border: 'none',
															borderRadius: '50%',
															width: '20px',
															height: '20px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															cursor: 'pointer'
														}}
														title="Remove image"
													>
														<X size={12} />
													</button>
												</div>
											))}
										</div>
									)}

										{packageImages.length > 0 && (
											<div style={{ fontSize: '12px', color: packageImages.length >= 2 ? '#10b981' : '#f59e0b' }}>
												{packageImages.length}/2 images uploaded {packageImages.length >= 2 ? '✓' : '(minimum 2 required)'}
											</div>
										)}
									</div>

									{/* Received Item Images Upload */}
									<div>
										<label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
											Images of Items Received from Seller
										</label>
									<div style={{
										border: '2px dashed #d1d5db',
										borderRadius: '8px',
										padding: '16px',
										textAlign: 'center',
										marginBottom: '12px'
									}}>
										<input
											type="file"
											multiple
											accept="image/*"
											onChange={handleReceivedItemImageUpload}
											style={{ display: 'none' }}
											id="received-item-image-upload"
										/>
										<label htmlFor="received-item-image-upload" style={{ cursor: 'pointer' }}>
											<Upload size={24} color="#6b7280" style={{ marginBottom: '8px' }} />
											<p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
												Upload item images
											</p>
											<p style={{ color: '#9ca3af', fontSize: '12px', margin: '4px 0 0' }}>
												Images of lehenga, dupatta, choli, etc. received from seller
											</p>
										</label>
									</div>

									{/* Received Item Images Preview */}
									{receivedItemImages.length > 0 && (
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
											gap: '8px',
											marginBottom: '12px'
										}}>
											{receivedItemImages.map((image, index) => (
												<div key={index} style={{ position: 'relative' }}>
													<img
														src={image.preview}
														alt={`Received Item ${index + 1}`}
														style={{
															width: '100%',
															height: '80px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb'
														}}
													/>
													<button
														type="button"
														onClick={() => removeReceivedItemImage(index)}
														style={{
															position: 'absolute',
															top: '4px',
															right: '4px',
															background: 'rgba(239, 68, 68, 0.9)',
															color: 'white',
															border: 'none',
															borderRadius: '50%',
															width: '20px',
															height: '20px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															cursor: 'pointer'
														}}
														title="Remove image"
													>
														<X size={12} />
													</button>
												</div>
											))}
										</div>
									)}
									</div>
								</div>
								<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
									<button
										onClick={closeModal}
										style={{
											backgroundColor: '#f3f4f6',
											color: '#111827',
											border: '1px solid #e5e7eb',
											padding: '10px 14px',
											borderRadius: '8px',
											cursor: 'pointer'
										}}
									>
										Cancel
									</button>
									<button
										onClick={handleConfirmReceived}
										disabled={submitting}
										style={{
											backgroundColor: submitting ? '#9ca3af' : '#2563eb',
											color: 'white',
											border: '1px solid ' + (submitting ? '#9ca3af' : '#2563eb'),
											padding: '10px 14px',
											borderRadius: '8px',
											cursor: submitting ? 'not-allowed' : 'pointer',
											display: 'flex',
											alignItems: 'center',
											gap: '8px'
										}}
									>
										{submitting && <RefreshCw size={16} className="animate-spin" />}
										{submitting ? 'Confirming...' : 'Confirm Received by Store'}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UpcomingDeliveries;
